import {
  BottomSheet,
  DialogFooter,
  SystemInlineBanner,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Link, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../../components/BottomSheetHeader";
import { Button } from "../../../components/Button";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { useWorkplaceModalsDataContext } from "../../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { MyShiftCard } from "../../Card/MyShiftCard";

export interface BundleBookedSheetProps {
  modalState: UseModalState;
}

export function BundleBookedSheet(props: BundleBookedSheetProps) {
  const { modalState } = props;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const { shift, bundle, bundleBookingResults, isForYouShift } = useShiftModalsDataContext();

  useLogEffect(
    APP_V2_USER_EVENTS.FOR_YOU_SHIFTS_SHIFT_BOOKED,
    {
      shiftId: shift?.id,
      workplaceId: shift?.workplace?.id,
    },
    {
      enabled:
        modalState.modalIsOpen &&
        isForYouShift &&
        isDefined(shift) &&
        isDefined(bundleBookingResults) &&
        bundleBookingResults.bookedShiftIds.includes(shift.id),
    }
  );

  const workplace = shift?.workplace;
  const workplaceTimezone = workplace?.attributes?.location?.timezone;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace?.id,
  });

  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels(
    isDefined(bundle) ? [bundle.qualification] : []
  );
  const [qualificationText] = qualificationDisplayLabels;

  const bookedShiftIds = bundleBookingResults?.bookedShiftIds ?? [];
  const failedShiftIds = bundleBookingResults?.failedShiftIds ?? [];

  const bookedShifts = (bundle?.shifts ?? []).filter((bundleShift) =>
    bookedShiftIds.includes(bundleShift.id)
  );

  const bookedCount = bookedShifts.length;
  const failedCount = failedShiftIds.length;

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{ height: "100%" }}
      header={
        <BottomSheetHeader
          onClose={() => {
            modalState.closeModal();
          }}
        />
      }
      footer={
        isDefined(workplace) ? (
          <DialogFooter orientation="vertical" LinkComponent={Link}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                const modalPath = generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
                  workplaceId: workplace.id,
                });
                modalState.closeModal();
                history.push(modalPath);
              }}
            >
              See workplace profile
            </Button>
          </DialogFooter>
        ) : undefined
      }
    >
      <DialogContent sx={{ flex: 1, minHeight: 0, overflowY: "auto", backgroundColor: "unset" }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          <Title variant="h2" component="h2" sx={{ textAlign: "center" }}>
            {bookedCount} {pluralize("shift", bookedCount)} booked!
          </Title>

          {failedCount > 0 && (
            <SystemInlineBanner
              iconType="triangle-exclamation"
              color="warning"
              sx={{ width: "100%" }}
            >
              <Text inline variant="body2" color="inherit">
                {failedCount} other selected {pluralize("shift", failedCount)} couldn&apos;t be
                confirmed
              </Text>
            </SystemInlineBanner>
          )}

          <Stack spacing={5} sx={{ width: "100%" }}>
            {bookedShifts.map((bookedShift) => (
              <MyShiftCard
                key={bookedShift.id}
                outlined
                shiftStart={bookedShift.attributes.start}
                shiftEnd={bookedShift.attributes.end}
                shiftTimeSlot={bookedShift.attributes.timeSlot}
                workplaceName={workplace?.attributes?.name}
                workplaceTimezone={workplaceTimezone}
                shiftFormattedTotalPay={formatCentsAsUsd(bookedShift.pay.totalPayInMinorUnits)}
                shiftTotalPay={bookedShift.pay.totalPayInMinorUnits / 100}
                shiftHourlyPay={formatCentsAsUsd(bookedShift.pay.hourlyPayInMinorUnits)}
                shiftBoost={undefined}
                requiresBreak={workplace?.attributes?.requiresLunchBreak}
                breakDurationInMinutes={mandatoryBreakDurationInMinutes}
                qualificationText={qualificationText ?? bundle?.qualification ?? ""}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
